import { ReactElement, useEffect, useState } from 'react';
import { useFeatureFlag } from '@revenuewell/front-end-bundle';
import { useNotifications } from '@revenuewell/uc-notifications-client';
import { PatientIvStatusButtonFactory } from '../../../../../insurance-verification/patient-flow/status-button/status-button-factory';
import { useEventBus } from '../../../../../../hooks/use-event-bus';
import { useInsuranceApi } from '../../../../../../hooks/use-insurance-api';
import { usePatientProfile } from '../../../../../../hooks/use-patient-profile';
import { PatientVerificationSummary } from '../../../../../../services/insurance/types/patient-verification-summary';
import { EventIvStatusUpdated } from '../../../../../../types/event-bus';
import { InfoTitle } from './overview.styles';

type InsuranceComponentProps = {
  type: 'primary' | 'secondary';
};

export const InsuranceComponent = ({ type }: InsuranceComponentProps): ReactElement | null => {
  const { patientInfo, contactDetails, locationId } = usePatientProfile();
  const { listen } = useEventBus();
  const { listen: listenNotification } = useNotifications();
  const [isIv2Active, setIsIv2Active] = useState<boolean>(false);
  const [patientIv, setPatientIv] = useState<PatientVerificationSummary>();
  const { hasFlag } = useFeatureFlag();
  const { getPatientVerificationSummaries, getPracticeOnboarding } = useInsuranceApi();

  const loadInsuranceVerifications = async () => {
    const patientVerifications = await getPatientVerificationSummaries([contactDetails.id], locationId);

    if (patientVerifications.length > 0) setPatientIv(patientVerifications[0]);
  };

  useEffect(() => {
    const initializeInsuranceVerification = async () => {
      if (hasFlag('InsuranceVerification2')) {
        const onboarding = await getPracticeOnboarding(locationId);
        const isActive = onboarding?.status === 'Active' && (onboarding?.isInsuranceVerificationDisplayActive || false);

        if (isActive) loadInsuranceVerifications();

        setIsIv2Active(isActive);
      }
    };

    initializeInsuranceVerification();
  }, [locationId]);

  useEffect(() => {
    return listen('ivStatusUpdated', (event) => {
      const { data } = event as EventIvStatusUpdated;

      if (patientIv && data.status === 'InsuranceInProcess') {
        setPatientIv({ ...patientIv, pendingIvPriority: data.priority });
      } else {
        loadInsuranceVerifications();
      }
    });
  }, [listen, patientIv, setPatientIv]);

  useEffect(() => {
    return listenNotification('insurance-verification-update', (event: { locationId?: number; locationid?: number }) => {
      const locId = event?.locationId || event?.locationid;

      if (locId === locationId) {
        loadInsuranceVerifications();
      }
    });
  }, [locationId, listenNotification]);

  if (type === 'primary') {
    if (isIv2Active && patientIv && (patientInfo?.insuranceInfo?.carrierId || patientIv.primaryVerificationSummary)) {
      return (
        <>
          <InfoTitle>Primary Insurance Status</InfoTitle>
          <PatientIvStatusButtonFactory
            patientId={patientInfo.id}
            locationId={contactDetails.location?.id}
            verificationPriority="Primary"
            selectedSummary={patientIv?.primaryVerificationSummary}
            patientSummary={patientIv}
          />
        </>
      );
    }

    return null;
  }

  if (type === 'secondary') {
    if (isIv2Active && patientIv && (patientInfo?.secondaryInsuranceInfo?.carrierId || patientIv.secondaryVerificationSummary)) {
      return (
        <>
          <InfoTitle>Secondary Insurance Status</InfoTitle>
          <PatientIvStatusButtonFactory
            patientId={contactDetails.id}
            locationId={contactDetails.location?.id}
            verificationPriority="Secondary"
            selectedSummary={patientIv?.secondaryVerificationSummary}
            patientSummary={patientIv}
          />
        </>
      );
    }

    return null;
  }

  return null;
};
